<template>
  <div class="puzzle-game" :class="{ 'game-over': gameOver }">
    <!-- 游戏区域 -->
    <div class="game-container" v-if="gameStarted">
      <div class="game-header">
        <div class="score-board">
          <div class="moves">移动次数: {{ moves }}</div>
          <div class="timer">时间: {{ formatTime(elapsedTime) }}</div>
        </div>
        <div class="puzzle-preview" @click="togglePreview">
          <img
            :src="puzzleImage"
            alt="拼图预览"
            :class="{ visible: showPreview }"
          />
          <div class="preview-label">
            {{ showPreview ? "隐藏预览" : "显示预览" }}
          </div>
        </div>
      </div>

      <!-- 游戏主界面 -->
      <div class="game-main">
        <div
          class="puzzle-container"
          :style="{ width: `${boardSize}px`, height: `${boardSize}px` }"
        >
          <div
            v-for="(piece, index) in puzzlePieces"
            :key="index"
            class="puzzle-piece"
            :class="{ empty: piece.isEmpty, correct: piece.correct }"
            :style="{
              top: `${piece.row * pieceSize}px`,
              left: `${piece.col * pieceSize}px`,
              width: `${pieceSize}px`,
              height: `${pieceSize}px`,
              backgroundImage: piece.isEmpty ? 'none' : `url(${puzzleImage})`,
              backgroundPosition: piece.isEmpty
                ? 'center'
                : `${-piece.originalCol * pieceSize}px ${-piece.originalRow *
                    pieceSize}px`,
              backgroundSize: `${boardSize}px ${boardSize}px`,
            }"
            @click="movePiece(index)"
          >
            <div class="piece-number" v-if="showNumbers && !piece.isEmpty">
              {{ piece.originalRow * gridSize + piece.originalCol + 1 }}
            </div>
          </div>
        </div>
      </div>

      <!-- 游戏控制 -->
      <div class="game-controls">
        <div class="control-buttons-container">
          <button @click="pauseGame" class="control-btn">
            {{ isPaused ? "继续" : "暂停" }}
          </button>
          <button @click="toggleNumbers" class="control-btn">
            {{ showNumbers ? "隐藏数字" : "显示数字" }}
          </button>
          <button @click="resetPuzzle" class="control-btn">
            重置
          </button>
          <button @click="backToStartScreen" class="control-btn">
            退出游戏
          </button>
        </div>
      </div>
    </div>

    <!-- 开始界面 -->
    <div class="start-screen" v-if="!gameStarted">
      <div class="start-content">
        <h1>拼图挑战🧩</h1>
        <p class="game-description">
          点击拼图块移动它们，将图片还原至正确位置！挑战最少的步数和最短的时间完成拼图。
        </p>

        <!-- 难度选择 UI -->
        <div class="difficulty-selection">
          <h3>选择难度</h3>
          <div class="difficulty-buttons">
            <button
              v-for="(level, index) in difficultyLevels"
              :key="index"
              @click="selectedDifficulty = index"
              class="difficulty-btn"
              :class="{ active: selectedDifficulty === index }"
            >
              {{ level }}
              <div class="difficulty-icon">{{ difficultyIcons[index] }}</div>
            </button>
          </div>
          <div class="difficulty-description">
            {{ difficultyDescriptions[selectedDifficulty] }}
          </div>
        </div>

        <!-- 拼图选择 -->
        <div class="puzzle-selection">
          <h3>选择拼图</h3>
          <div class="puzzle-options">
            <div
              v-for="(image, index) in puzzleOptions"
              :key="index"
              class="puzzle-option"
              :class="{ active: selectedPuzzle === index }"
              @click="selectedPuzzle = index"
            >
              <img :src="image" alt="拼图选项" />
            </div>
          </div>
        </div>

        <button @click="startGame" class="start-btn">开始游戏</button>
        <button @click="backToMap" class="back-btn">返回</button>
      </div>
    </div>

    <!-- 游戏结束界面 -->
    <div class="game-over-screen" v-if="gameOver">
      <div class="game-over-content">
        <h2>恭喜完成挑战！🎉</h2>

        <div class="puzzle-report">
          <h3>拼图报告</h3>

          <div class="report-grid">
            <div class="report-item">
              <div class="report-icon">🧩</div>
              <div class="report-label">难度</div>
              <div class="report-value">
                {{ difficultyLevels[selectedDifficulty] }}
              </div>
            </div>

            <div class="report-item">
              <div class="report-icon">🚶</div>
              <div class="report-label">移动次数</div>
              <div class="report-value">{{ moves }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">⏱️</div>
              <div class="report-label">完成时间</div>
              <div class="report-value">{{ formatTime(elapsedTime) }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">🏆</div>
              <div class="report-label">最佳记录</div>
              <div class="report-value">{{ getBestRecord() }}</div>
            </div>
          </div>
        </div>

        <div class="completed-puzzle">
          <img :src="puzzleImage" alt="完成的拼图" />
        </div>

        <div class="game-over-buttons">
          <button @click="restartGame" class="restart-btn">再玩一次</button>
          <button @click="backToStartScreen" class="back-btn">返回</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PuzzleGame",
  data() {
    return {
      gameStarted: false,
      gameOver: false,
      isPaused: false,
      showPreview: false,
      showNumbers: true,
      moves: 0,
      startTime: null,
      elapsedTime: 0,
      timerInterval: null,
      pauseStartTime: null,
      totalPausedTime: 0,
      puzzlePieces: [],
      emptyIndex: 0,
      gridSize: 3,
      pieceSize: 100,
      boardSize: 300,
      levelId: "",
      difficultyLevels: ["简单", "中等", "困难"],
      difficultyIcons: ["🌱", "🌿", "🔥"],
      difficultyDescriptions: [
        "2×2 网格，4块拼图，轻松上手",
        "3×3 网格，9块拼图，适合初学者",
        "4×4 网格，16块拼图，有一定挑战性",
      ],
      selectedDifficulty: 0,
      puzzleOptions: [
        "https://wugecdn.steam.fun/typefun/images/puzzle/joker.png",
        "https://wugecdn.steam.fun/typefun/images/puzzle/chicken.png",
        "https://wugecdn.steam.fun/typefun/images/puzzle/crab.png",
        "https://wugecdn.steam.fun/typefun/images/puzzle/butterfly.png",
      ],
      selectedPuzzle: 0,
      bestRecords: {},
    };
  },
  computed: {
    puzzleImage() {
      return this.puzzleOptions[this.selectedPuzzle];
    },
  },
  mounted() {
    this.getRouteParams();
    this.loadBestRecords();
  },
  beforeDestroy() {
    this.stopTimer();
    window.removeEventListener("keydown", this.handleKeyPress);
  },
  methods: {
    getRouteParams() {
      const { levelId } = this.$route.query;
      this.levelId = levelId || "intro-mouse";
    },

    loadBestRecords() {
      const storedRecords = localStorage.getItem(
        `puzzleRecords_${this.levelId}`
      );
      if (storedRecords) {
        this.bestRecords = JSON.parse(storedRecords);
      }
    },

    saveBestRecord() {
      const difficultyKey = `${this.selectedDifficulty}_${this.selectedPuzzle}`;
      const currentRecord = this.bestRecords[difficultyKey];

      if (
        !currentRecord ||
        this.moves < currentRecord.moves ||
        (this.moves === currentRecord.moves &&
          this.elapsedTime < currentRecord.time)
      ) {
        this.bestRecords[difficultyKey] = {
          moves: this.moves,
          time: this.elapsedTime,
        };

        localStorage.setItem(
          `puzzleRecords_${this.levelId}`,
          JSON.stringify(this.bestRecords)
        );
      }
    },

    getBestRecord() {
      const difficultyKey = `${this.selectedDifficulty}_${this.selectedPuzzle}`;
      const record = this.bestRecords[difficultyKey];

      if (record) {
        return `${record.moves}步 / ${this.formatTime(record.time)}`;
      }

      return "无记录";
    },

    startGame() {
      this.gameStarted = true;
      this.gameOver = false;
      this.isPaused = false;
      this.moves = 0;
      this.elapsedTime = 0;
      this.totalPausedTime = 0;
      this.pauseStartTime = null;
      this.gridSize = [2, 3, 4][this.selectedDifficulty];
      this.pieceSize = Math.floor(300 / this.gridSize);
      this.boardSize = this.pieceSize * this.gridSize;

      this.createPuzzle();
      this.startTimer();

      window.addEventListener("keydown", this.handleKeyPress);
    },

    createPuzzle() {
      this.puzzlePieces = [];

      // 创建有序拼图
      for (let row = 0; row < this.gridSize; row++) {
        for (let col = 0; col < this.gridSize; col++) {
          const isLast = row === this.gridSize - 1 && col === this.gridSize - 1;

          this.puzzlePieces.push({
            row,
            col,
            originalRow: row,
            originalCol: col,
            isEmpty: isLast,
            correct: !isLast, // 初始时所有非空白块都是正确的
          });
        }
      }

      // 记住空白块的索引
      this.emptyIndex = this.puzzlePieces.length - 1;

      // 打乱拼图 (确保可解)
      this.shufflePuzzle();
    },

    shufflePuzzle() {
      // 减少随机移动次数，使拼图更容易解决
      const difficultyFactor = [3, 5, 10][this.selectedDifficulty];
      const moves = Math.max(
        15,
        this.gridSize * this.gridSize * difficultyFactor
      );

      for (let i = 0; i < moves; i++) {
        const possibleMoves = this.getPossibleMoves();
        if (possibleMoves.length > 0) {
          const randomIndex = Math.floor(Math.random() * possibleMoves.length);
          this.swapWithEmpty(possibleMoves[randomIndex], false);
        }
      }

      // 确保不会出现已解决的拼图
      if (
        this.puzzlePieces.every(
          (piece) =>
            piece.isEmpty ||
            (piece.row === piece.originalRow && piece.col === piece.originalCol)
        )
      ) {
        // 如果随机后拼图恰好是完成状态，再随机移动几次
        for (let i = 0; i < 3; i++) {
          const possibleMoves = this.getPossibleMoves();
          if (possibleMoves.length > 0) {
            const randomIndex = Math.floor(
              Math.random() * possibleMoves.length
            );
            this.swapWithEmpty(possibleMoves[randomIndex], false);
          }
        }
      }

      // 重置移动计数
      this.moves = 0;

      // 更新所有块的correct属性
      this.updateCorrectPositions();
    },

    getPossibleMoves() {
      const possibleMoves = [];
      const emptyPiece = this.puzzlePieces[this.emptyIndex];

      for (let i = 0; i < this.puzzlePieces.length; i++) {
        const piece = this.puzzlePieces[i];

        // 判断是否相邻
        if (
          (Math.abs(piece.row - emptyPiece.row) === 1 &&
            piece.col === emptyPiece.col) ||
          (Math.abs(piece.col - emptyPiece.col) === 1 &&
            piece.row === emptyPiece.row)
        ) {
          possibleMoves.push(i);
        }
      }

      return possibleMoves;
    },

    movePiece(index) {
      if (this.isPaused || this.gameOver) return;

      const possibleMoves = this.getPossibleMoves();
      if (possibleMoves.includes(index)) {
        this.swapWithEmpty(index, true);
        this.updateCorrectPositions();
        this.checkWinCondition();
      }
    },

    swapWithEmpty(index, countMove = true) {
      // 交换位置
      const temp = {
        row: this.puzzlePieces[index].row,
        col: this.puzzlePieces[index].col,
      };
      this.puzzlePieces[index].row = this.puzzlePieces[this.emptyIndex].row;
      this.puzzlePieces[index].col = this.puzzlePieces[this.emptyIndex].col;
      this.puzzlePieces[this.emptyIndex].row = temp.row;
      this.puzzlePieces[this.emptyIndex].col = temp.col;

      // 播放移动音效
      if (countMove) {
        this.playMoveSound();
        this.moves++;
      }
    },

    updateCorrectPositions() {
      for (let i = 0; i < this.puzzlePieces.length; i++) {
        const piece = this.puzzlePieces[i];
        if (!piece.isEmpty) {
          piece.correct =
            piece.row === piece.originalRow && piece.col === piece.originalCol;
        }
      }
    },

    checkWinCondition() {
      const allCorrect = this.puzzlePieces.every(
        (piece) => piece.isEmpty || piece.correct
      );

      if (allCorrect) {
        this.gameOver = true;
        this.stopTimer();
        this.saveBestRecord();
        this.playWinSound();

        // 记录完成关卡
        const completedLevels = JSON.parse(
          localStorage.getItem("completedLevels") || "[]"
        );
        if (!completedLevels.includes(this.levelId)) {
          completedLevels.push(this.levelId);
          localStorage.setItem(
            "completedLevels",
            JSON.stringify(completedLevels)
          );
        }
      }
    },

    handleKeyPress(event) {
      if (this.isPaused || this.gameOver) return;

      const emptyPiece = this.puzzlePieces[this.emptyIndex];
      let targetIndex = -1;

      switch (event.key) {
        case "ArrowUp":
          // 查找下方的块，将其向上移动（空白块下移）
          targetIndex = this.puzzlePieces.findIndex(
            (p) => p.row === emptyPiece.row + 1 && p.col === emptyPiece.col
          );
          break;
        case "ArrowDown":
          // 查找上方的块，将其向下移动（空白块上移）
          targetIndex = this.puzzlePieces.findIndex(
            (p) => p.row === emptyPiece.row - 1 && p.col === emptyPiece.col
          );
          break;
        case "ArrowLeft":
          // 查找右侧的块，将其向左移动（空白块右移）
          targetIndex = this.puzzlePieces.findIndex(
            (p) => p.row === emptyPiece.row && p.col === emptyPiece.col + 1
          );
          break;
        case "ArrowRight":
          // 查找左侧的块，将其向右移动（空白块左移）
          targetIndex = this.puzzlePieces.findIndex(
            (p) => p.row === emptyPiece.row && p.col === emptyPiece.col - 1
          );
          break;
      }

      if (targetIndex !== -1) {
        this.movePiece(targetIndex);
      }
    },

    startTimer() {
      this.startTime = Date.now();
      this.timerInterval = setInterval(() => {
        if (!this.isPaused) {
          this.elapsedTime = Math.floor(
            (Date.now() - this.startTime - this.totalPausedTime) / 1000
          );
        }
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timerInterval);
    },

    pauseGame() {
      this.isPaused = !this.isPaused;

      if (this.isPaused) {
        this.pauseStartTime = Date.now();
      } else {
        this.totalPausedTime += Date.now() - this.pauseStartTime;
        this.pauseStartTime = null;
      }
    },

    resetPuzzle() {
      this.createPuzzle();
      this.stopTimer();
      this.startTimer();
      this.moves = 0;
      this.elapsedTime = 0;
      this.totalPausedTime = 0;
      this.pauseStartTime = null;
    },

    togglePreview() {
      this.showPreview = !this.showPreview;
    },

    toggleNumbers() {
      this.showNumbers = !this.showNumbers;
    },

    restartGame() {
      this.startGame();
    },

    exitGame() {
      this.stopTimer();
      this.backToMap();
    },

    backToMap() {
      this.$router.go(-1);
    },

    backToStartScreen() {
      this.gameOver = false;
      this.gameStarted = false;
      this.stopTimer();
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds
      ).padStart(2, "0")}`;
    },

    playMoveSound() {
      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/click.mp3"
      );
      audio.volume = 0.3;
      audio.play();
    },

    playWinSound() {
      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/win.mp3"
      );
      audio.play();
    },
  },
};
</script>

<style scoped>
.puzzle-game {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  height: 100vh;
      background: linear-gradient(135deg, #1a2a6c, #3a8ee6, #fdbb2d);
  color: white;
  font-family: "Arial", sans-serif;
  position: fixed;
  top: 60;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  overflow: hidden;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 800px;
  height: 100vh;
  padding: 20px;
  position: relative;
  z-index: 100;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.score-board {
  display: flex;
  flex-direction: column;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px 20px;
  border-radius: 10px;
  min-width: 150px;
}

.puzzle-preview {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.puzzle-preview:hover {
  transform: scale(1.05);
}

.puzzle-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.puzzle-preview img.visible {
  opacity: 1;
}

.preview-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 4px;
  font-size: 12px;
  text-align: center;
}

.game-main {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.puzzle-container {
  position: relative;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.puzzle-piece {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-size: cover;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.puzzle-piece:hover {
  transform: scale(0.98);
  box-shadow: inset 0 0 15px rgba(255, 255, 255, 0.2);
}

.puzzle-piece.empty {
  background-color: rgba(0, 0, 0, 0.3);
  border: none;
  box-shadow: none;
  cursor: default;
}

.puzzle-piece.empty:hover {
  transform: none;
}

.puzzle-piece.correct {
  box-shadow: inset 0 0 8px rgba(0, 255, 0, 0.3);
}

.piece-number {
  font-size: 16px;
  font-weight: bold;
  color: white;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.game-controls {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
}

.control-buttons-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.control-btn {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

/* 开始屏幕样式 */
.start-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a2a6c, #3a8ee6, #f8c46f);
  z-index: 10000;
}

.start-content {
  background: rgba(0, 0, 0, 0.6);
  border-radius: 15px;
  padding: 25px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.start-content h1 {
  margin-bottom: 15px;
  font-size: 28px;
  background: linear-gradient(135deg, #1a2a6c, #ffffff, #f8c46f);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 1px;
}

.game-description {
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 20px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.difficulty-selection,
.puzzle-selection {
  margin-bottom: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 10px;
}

.difficulty-selection h3,
.puzzle-selection h3 {
  margin-bottom: 10px;
  font-size: 16px;
  color: #f6d365;
}

.difficulty-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.difficulty-btn {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}

.difficulty-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
}

.difficulty-btn.active {
  background: rgba(246, 211, 101, 0.2);
  border-color: rgba(246, 211, 101, 0.5);
  box-shadow: 0 0 15px rgba(246, 211, 101, 0.3);
  transform: translateY(-3px) scale(1.05);
}

.difficulty-icon {
  font-size: 18px;
  margin-top: 4px;
}

.difficulty-description {
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 8px;
  min-height: 30px;
  padding: 0 5px;
  line-height: 1.2;
}

.puzzle-options {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.puzzle-option {
  width: 65px;
  height: 65px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.puzzle-option.active {
  border-color: #f6d365;
  box-shadow: 0 0 15px rgba(246, 211, 101, 0.5);
  transform: scale(1.1);
}

.puzzle-option:hover {
  transform: scale(1.05);
}

.puzzle-option img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.start-btn,
.back-btn {
  padding: 10px 20px;
  font-size: 14px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 8px 6px;
  font-weight: 600;
  letter-spacing: 1px;
}

.start-btn {
  background: linear-gradient(135deg, #f6d365, #fda085);
  color: #333;
  box-shadow: 0 5px 15px rgba(246, 211, 101, 0.4);
}

.back-btn {
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.start-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(246, 211, 101, 0.6);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

/* 游戏结束屏幕样式 */
.game-over-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  backdrop-filter: blur(5px);
}

.game-over-content {
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  padding: 25px;
  max-width: 450px;
  width: 85%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.game-over-content h2 {
  margin-bottom: 15px;
  font-size: 26px;
  color: #f6d365;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.puzzle-report {
  margin-bottom: 20px;
}

.puzzle-report h3 {
  margin-bottom: 15px;
  font-size: 18px;
  color: white;
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.report-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;
  transition: transform 0.2s ease;
}

.report-item:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.08);
}

.report-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.report-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 3px;
}

.report-value {
  font-size: 16px;
  font-weight: bold;
}

.completed-puzzle {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease;
}

.completed-puzzle:hover {
  transform: scale(1.05);
}

.completed-puzzle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.game-over-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.restart-btn,
.back-btn {
  padding: 10px 20px;
  font-size: 14px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
}

.restart-btn {
  background: linear-gradient(135deg, #f6d365, #fda085);
  color: #333;
}

.restart-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(246, 211, 101, 0.3);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .start-content {
    padding: 20px;
    width: 85%;
  }

  .start-content h1 {
    font-size: 24px;
  }

  .game-description {
    font-size: 14px;
  }

  .difficulty-buttons {
    flex-wrap: wrap;
  }

  .puzzle-container {
    width: 280px;
    height: 280px;
  }

  .game-over-content {
    padding: 20px;
    max-width: 320px;
  }

  .game-over-content h2 {
    font-size: 22px;
  }

  .completed-puzzle {
    width: 120px;
    height: 120px;
  }

  .report-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .report-item {
    padding: 8px 5px;
  }

  .report-icon {
    font-size: 20px;
  }

  .report-value {
    font-size: 14px;
  }
}
</style>
